/**
 * Accounts + membership endpoints (AB#331) and seat checkout (AB#334).
 *
 * Contract notes for the invites-UI wave:
 * - Accounts are addressed by `pid` (uuid). Non-members get 404 (IDOR-safe).
 * - Mutations are owner-only (403 `owner_only` for members).
 * - Adding beyond the member cap fails closed with 402 `seat_limit_reached`;
 *   `error.details` carries { member_cap, member_count, seat_price, checkout_url }.
 * - `addMember` is dual-purpose (AB#451): a registered email is added directly
 *   (returns the member); an unregistered email is emailed an invitation
 *   (returns { invited: true, email }). Re-inviting a still-pending email is a
 *   409 `already_invited`.
 */

import { api } from './client';
import type { ApiResponse } from '../types';

export interface Account {
  pid: string;
  name: string;
  /** The current user's role in this account: 'owner' | 'member'. */
  role: string;
  member_count: number;
  /** Fail-closed cap: 2 (owner + 1 included) + purchased active seats. */
  member_cap: number;
  extra_seats: number;
  /**
   * AB#602: the account's active seat(s) are cancelled-but-in-grace (won't renew
   * but still bill until period end). Additive — absent on older backends.
   */
  seat_cancelled?: boolean;
  /** AB#602: ISO timestamp the cancelled seat's paid period ends, or null. */
  seat_ends_at?: string | null;
}

export interface AccountMember {
  user_pid: string;
  email: string;
  name: string;
  role: string;
  is_you?: boolean;
  /** When this member joined the account (ISO timestamp). */
  joined_at?: string;
}

/**
 * Result of `addMember` (AB#451). Either a member was added directly (member
 * fields present) or an invitation was emailed (`invited: true` + `email`).
 * All fields optional so callers branch on `invited`.
 */
export interface AddMemberResult {
  /** True when the email wasn't a registered user — an invite was emailed instead. */
  invited?: boolean;
  user_pid?: string;
  email?: string;
  name?: string;
  role?: string;
  joined_at?: string;
}

/** A pending (unaccepted, unexpired) invitation to join an account (AB#451). */
export interface AccountInvitation {
  id: number;
  email: string;
  role: string;
  created_at: string;
  expires_at: string;
}

/** Result of accepting an invitation (AB#451). */
export interface AcceptInvitationResult {
  account_pid: string;
  role: string;
}

export interface SeatCheckoutResponse {
  /** RevenueCat seat checkout URL (empty until configured). */
  url: string;
  account_pid: string;
  seat_price: string;
}

export const accountsApi = {
  /** All accounts the current user belongs to (personal account auto-seeded). */
  list: () => api.get<ApiResponse<Account[]>>('/accounts'),

  /** Create a new account; the caller becomes its owner. */
  create: (name: string) => api.post<ApiResponse<Account>>('/accounts', { name }),

  /** Roster of an account (any member). */
  members: (accountPid: string) =>
    api.get<ApiResponse<AccountMember[]>>(`/accounts/${encodeURIComponent(accountPid)}/members`),

  /**
   * Add a member by email (owner only, fail-closed at the cap). A registered
   * email is added directly; an unregistered one is emailed an invite. Branch
   * on `result.data?.invited` (AB#451).
   */
  addMember: (accountPid: string, email: string) =>
    api.post<ApiResponse<AddMemberResult>>(
      `/accounts/${encodeURIComponent(accountPid)}/members`,
      { email },
    ),

  /** Pending invitations for an account (owner only). */
  invitations: (accountPid: string) =>
    api.get<ApiResponse<AccountInvitation[]>>(
      `/accounts/${encodeURIComponent(accountPid)}/invitations`,
    ),

  /** Revoke (void) a pending invitation (owner only; 404 if not found). */
  revokeInvitation: (accountPid: string, invitationId: number) =>
    api.post<ApiResponse<{ revoked: boolean }>>(
      `/accounts/${encodeURIComponent(accountPid)}/invitations/${invitationId}/revoke`,
      {},
    ),

  /** Re-email a pending invitation and restart its expiry (owner only, AB#1015). */
  resendInvitation: (accountPid: string, invitationId: number) =>
    api.post<ApiResponse<{ resent: boolean; email: string }>>(
      `/accounts/${encodeURIComponent(accountPid)}/invitations/${invitationId}/resend`,
      {},
    ),

  /** Accept an invitation with its token (the invited user's own action). */
  acceptInvitation: (token: string) =>
    api.post<ApiResponse<AcceptInvitationResult>>('/invitations/accept', { token }),

  /** Remove a member (owner only; the owner cannot be removed). */
  removeMember: (accountPid: string, userPid: string) =>
    api.delete<ApiResponse<{ removed: boolean }>>(
      `/accounts/${encodeURIComponent(accountPid)}/members/${encodeURIComponent(userPid)}`,
    ),

  /** Leave an account you're a member of (AB#1018). 400 `owner_cannot_leave` for owners. */
  leave: (accountPid: string) =>
    api.post<ApiResponse<{ left: boolean; account_pid: string }>>(
      `/accounts/${encodeURIComponent(accountPid)}/leave`,
      {},
    ),

  /** Seat checkout link for an account ($1.99/mo per seat, owner only). */
  seatCheckout: (accountPid: string) =>
    api.get<ApiResponse<SeatCheckoutResponse>>(
      `/billing/checkout/seat/${encodeURIComponent(accountPid)}`,
    ),

  /**
   * Preview self-deletion of the calling user (AB#454, step 1). No side effects:
   * returns the plan/seats that WOULD be cancelled + a confirmation message. May
   * fail with 409 `sole_owner_block` if the user solely owns shared accounts.
   */
  selfDeletePreview: () =>
    api.post<ApiResponse<SelfDeletePreview>>('/accounts/self-delete', {}),

  /**
   * Confirm self-deletion (AB#454, step 2). Cancels billing (fail-closed) then
   * soft-deletes the user + their solo accounts. Irreversible from the UI.
   */
  selfDeleteConfirm: () =>
    api.post<ApiResponse<{ deleted: boolean }>>('/accounts/self-delete?confirm=true', {}),
};

/** Preview payload of the self-delete two-step (AB#454). */
export interface SelfDeletePreview {
  requires_confirmation: boolean;
  /** Human-readable list of things that will be cancelled (plan, seats). */
  cancellations: string[];
  message: string;
}
